<div class="inspection-config-page">
  <header class="page-header">
    <div>
      <wm-common-text
        class="page-title"
        role="heading"
        aria-level="1"
        [data]="{ text: 'Inspection App Configuration' }"
      ></wm-common-text>
      <wm-common-text
        class="page-subtitle"
        [data]="{ text: 'Configure the data shown in the Inspection Tablet App\'s home page and Application Details panel. Each item supports a fallback list of fields resolved in priority order &mdash; the first mapping with a non-empty value is used.' }"
      ></wm-common-text>
    </div>
    <span *ngIf="isDirty" class="unsaved-pill">
      <span class="material-icons">warning_amber</span>
      <wm-common-text class="unsaved-text" [data]="{ text: 'Unsaved changes' }"></wm-common-text>
    </span>
  </header>

  <app-loader *ngIf="isLoading"></app-loader>

  <main class="configuration-stack" [class.loading]="isLoading">
    <section class="configuration-card">
      <div class="section-header">
        <div>
          <wm-common-text class="section-title" role="heading" aria-level="2" [data]="{ text: 'Home Page Columns' }"></wm-common-text>
          <wm-common-text class="section-description" [data]="{ text: 'Columns shown on the Inspection Tablet App\'s home page list. Drag to reorder columns or fallback fields.' }"></wm-common-text>
        </div>
        <span class="item-count"><wm-common-text class="count-text" [data]="{ text: homePage.columns.length + (homePage.columns.length === 1 ? ' COLUMN' : ' COLUMNS') }"></wm-common-text></span>
      </div>

      <div class="add-item-row">
        <wm-common-textfield
          class="new-label-field"
          [(ngModel)]="homePage.newColumnLabel"
          [data]="{ label: '', placeholder: 'New column label...', value: homePage.newColumnLabel }"
          [disabled]="isLoading"
          (keyup.enter)="addColumn(homePage)"
        ></wm-common-textfield>
        <button
          type="button"
          class="primary-action"
          [disabled]="isLoading || !homePage.newColumnLabel.trim()"
          (click)="addColumn(homePage)"
        >
          <span class="material-icons">add</span>
          <wm-common-text class="action-text" [data]="{ text: 'ADD COLUMN' }"></wm-common-text>
        </button>
      </div>

      <wm-common-text *ngIf="homePage.columns.length" class="drag-help" [data]="{ text: 'Fallback fields priority appear highest to lowest.' }"></wm-common-text>

      <!-- Added by Amisha on 2026-10-07.
           Reason: delay touch dragging briefly so swiping the phone-width column list does not accidentally reorder cards. -->
      <div
        *ngIf="homePage.columns.length"
        cdkDropList
        cdkDropListOrientation="horizontal"
        [cdkDropListData]="homePage.columns"
        (cdkDropListDropped)="dropColumn($event, homePage)"
        class="home-columns"
      >
        <article
          *ngFor="let column of homePage.columns; let columnIndex = index"
          cdkDrag
          [cdkDragStartDelay]="{ touch: 180, mouse: 0 }"
          class="item-card home-column-card"
        >
          <div class="item-card-header">
            <span cdkDragHandle class="drag-handle" title="Drag to reorder">
              <span class="material-icons">drag_indicator</span>
            </span>
            <wm-common-text class="item-number" [data]="{ text: (columnIndex + 1) + '.' }"></wm-common-text>
            <wm-common-textfield
              class="label-field"
              [(ngModel)]="column.label"
              (ngModelChange)="isDirty = true"
              [data]="{ label: '', placeholder: 'Column label', value: column.label }"
              [disabled]="isLoading"
            ></wm-common-textfield>
            <button
              type="button"
              class="icon-action"
              title="Remove column"
              [disabled]="isLoading"
              (click)="requestDeleteColumn(homePage, columnIndex)"
            >
              <span class="material-icons">delete_outline</span>
            </button>
          </div>

          <div class="item-card-body">
            <div
              cdkDropList
              [cdkDropListData]="column.mappings"
              (cdkDropListDropped)="dropMapping($event, homePage, columnIndex)"
              class="mapping-list"
            >
              <div
                *ngFor="let mapping of column.mappings; let mappingIndex = index"
                cdkDrag
                [cdkDragStartDelay]="{ touch: 180, mouse: 0 }"
                class="mapping-row"
              >
                <span cdkDragHandle class="mapping-drag-handle">
                  <span class="material-icons">drag_indicator</span>
                </span>
                <div class="mapping-copy">
                  <wm-common-text class="mapping-label" [data]="{ text: getConfiguredFieldLabel(mapping.fieldKey, mapping.source) }"></wm-common-text>
                  <wm-common-text class="mapping-source" [data]="{ text: getSourceLabel(mapping.source) }"></wm-common-text>
                </div>
                <button
                  type="button"
                  class="icon-action compact"
                  title="Remove fallback field"
                  [disabled]="isLoading"
                  (click)="removeMapping(homePage, columnIndex, mappingIndex)"
                >
                  <span class="material-icons">remove_circle_outline</span>
                </button>
              </div>
              <div *ngIf="!column.mappings.length" class="empty-mapping">
                <wm-common-text class="empty-mapping-text" [data]="{ text: 'No fallback fields added.' }"></wm-common-text>
              </div>
            </div>

            <div ngbDropdown container="body" placement="bottom-start top-start" #homeDrop="ngbDropdown">
              <button
                type="button"
                class="add-fallback-button"
                ngbDropdownToggle
                [disabled]="isLoading"
              >
                <span class="material-icons">add</span>
                <wm-common-text class="action-text" [data]="{ text: 'ADD FALLBACK FIELD' }"></wm-common-text>
              </button>
              <div ngbDropdownMenu class="janus-field-menu">
                <ng-container *ngTemplateOutlet="fieldOptions; context: { section: homePage, columnIndex: columnIndex, dropdown: homeDrop }"></ng-container>
              </div>
            </div>
          </div>
        </article>
      </div>

      <div *ngIf="!homePage.columns.length && !isLoading" class="empty-state">
        <span class="material-icons">view_column</span>
        <div>
          <wm-common-text class="empty-title" [data]="{ text: 'No home page columns configured' }"></wm-common-text>
          <wm-common-text class="section-description" [data]="{ text: 'Enter a label above to add the first column.' }"></wm-common-text>
        </div>
      </div>
    </section>

    <section class="configuration-card">
      <div class="section-header">
        <div>
          <wm-common-text class="section-title" role="heading" aria-level="2" [data]="{ text: 'Application Details' }"></wm-common-text>
          <wm-common-text class="section-description" [data]="{ text: 'Fields shown when selecting a scheduled inspection that was downloaded to the tablet app. Each detail item supports a fallback list of fields resolved in priority order. Drag to reorder items or fallback fields.' }"></wm-common-text>
        </div>
        <span class="item-count"><wm-common-text class="count-text" [data]="{ text: appDetails.columns.length + (appDetails.columns.length === 1 ? ' ITEM' : ' ITEMS') }"></wm-common-text></span>
      </div>

      <div class="add-item-row">
        <wm-common-textfield
          class="new-label-field"
          [(ngModel)]="appDetails.newColumnLabel"
          [data]="{ label: '', placeholder: 'New detail label...', value: appDetails.newColumnLabel }"
          [disabled]="isLoading"
          (keyup.enter)="addColumn(appDetails)"
        ></wm-common-textfield>
        <button
          type="button"
          class="primary-action"
          [disabled]="isLoading || !appDetails.newColumnLabel.trim()"
          (click)="addColumn(appDetails)"
        >
          <span class="material-icons">add</span>
          <wm-common-text class="action-text" [data]="{ text: 'ADD DETAIL' }"></wm-common-text>
        </button>
      </div>

      <wm-common-text *ngIf="appDetails.columns.length" class="drag-help" [data]="{ text: 'Fallback fields priority appear highest to lowest.' }"></wm-common-text>

      <div
        *ngIf="appDetails.columns.length"
        cdkDropList
        [cdkDropListData]="appDetails.columns"
        (cdkDropListDropped)="dropColumn($event, appDetails)"
        class="details-list"
      >
        <article
          *ngFor="let column of appDetails.columns; let columnIndex = index"
          cdkDrag
          [cdkDragStartDelay]="{ touch: 180, mouse: 0 }"
          class="item-card detail-card"
        >
          <div class="item-card-header">
            <span cdkDragHandle class="drag-handle" title="Drag to reorder">
              <span class="material-icons">drag_indicator</span>
            </span>
            <wm-common-text class="item-number" [data]="{ text: (columnIndex + 1) + '.' }"></wm-common-text>
            <wm-common-textfield
              class="label-field"
              [(ngModel)]="column.label"
              (ngModelChange)="isDirty = true"
              [data]="{ label: '', placeholder: 'Detail label', value: column.label }"
              [disabled]="isLoading"
            ></wm-common-textfield>
            <button
              type="button"
              class="icon-action"
              title="Remove detail item"
              [disabled]="isLoading"
              (click)="requestDeleteColumn(appDetails, columnIndex)"
            >
              <span class="material-icons">delete_outline</span>
            </button>
          </div>

          <div class="detail-card-content">
            <div
              cdkDropList
              [cdkDropListData]="column.mappings"
              (cdkDropListDropped)="dropMapping($event, appDetails, columnIndex)"
              class="mapping-list"
            >
              <div
                *ngFor="let mapping of column.mappings; let mappingIndex = index"
                cdkDrag
                [cdkDragStartDelay]="{ touch: 180, mouse: 0 }"
                class="mapping-row"
              >
                <span cdkDragHandle class="mapping-drag-handle">
                  <span class="material-icons">drag_indicator</span>
                </span>
                <div class="mapping-copy">
                  <wm-common-text class="mapping-label" [data]="{ text: getConfiguredFieldLabel(mapping.fieldKey, mapping.source) }"></wm-common-text>
                  <wm-common-text class="mapping-source" [data]="{ text: getSourceLabel(mapping.source) }"></wm-common-text>
                </div>
                <button
                  type="button"
                  class="icon-action compact"
                  title="Remove fallback field"
                  [disabled]="isLoading"
                  (click)="removeMapping(appDetails, columnIndex, mappingIndex)"
                >
                  <span class="material-icons">remove_circle_outline</span>
                </button>
              </div>
              <div *ngIf="!column.mappings.length" class="empty-mapping">
                <wm-common-text class="empty-mapping-text" [data]="{ text: 'No fallback fields added.' }"></wm-common-text>
              </div>
            </div>

            <div ngbDropdown container="body" placement="bottom-start top-start" #detailDrop="ngbDropdown">
              <button
                type="button"
                class="add-fallback-button"
                ngbDropdownToggle
                [disabled]="isLoading"
              >
                <span class="material-icons">add</span>
                <wm-common-text class="action-text" [data]="{ text: 'ADD FALLBACK FIELD' }"></wm-common-text>
              </button>
              <div ngbDropdownMenu class="janus-field-menu">
                <ng-container *ngTemplateOutlet="fieldOptions; context: { section: appDetails, columnIndex: columnIndex, dropdown: detailDrop }"></ng-container>
              </div>
            </div>
          </div>
        </article>
      </div>

      <div *ngIf="!appDetails.columns.length && !isLoading" class="empty-state">
        <span class="material-icons">description</span>
        <div>
          <wm-common-text class="empty-title" [data]="{ text: 'No application details configured' }"></wm-common-text>
          <wm-common-text class="section-description" [data]="{ text: 'Enter a label above to add the first detail item.' }"></wm-common-text>
        </div>
      </div>
    </section>

    <div class="page-actions">
      <wm-common-button class="footer-action close-action" [data]="{ text: 'Close', variant: 'basic', disabled: isLoading, onClick: close.bind(this) }"></wm-common-button>
      <wm-common-button class="footer-action delete-action" [data]="{ text: 'Delete', variant: 'basic', disabled: isLoading || !hasAnyConfiguration(), onClick: requestDeleteConfiguration.bind(this) }"></wm-common-button>
      <wm-common-button class="footer-action save-action" [data]="{ text: 'Save', variant: 'basic', disabled: isLoading || !isDirty, onClick: save.bind(this) }"></wm-common-button>
    </div>
  </main>
</div>

<ng-template #fieldOptions let-section="section" let-columnIndex="columnIndex" let-dropdown="dropdown">
  <ng-container *ngIf="getFieldsForMapping(section, columnIndex, FieldKeyItemSource.Application).length">
    <div class="dropdown-header"><wm-common-text class="dropdown-group-text" [data]="{ text: 'System Application Fields' }"></wm-common-text></div>
    <button
      *ngFor="let field of getFieldsForMapping(section, columnIndex, FieldKeyItemSource.Application)"
      type="button"
      ngbDropdownItem
      (click)="addFieldMapping(section, columnIndex, field); dropdown.close()"
    ><wm-common-text class="dropdown-option-text" [data]="{ text: field.label }"></wm-common-text></button>
  </ng-container>
  <ng-container *ngIf="getFieldsForMapping(section, columnIndex, FieldKeyItemSource.Inspection).length">
    <div class="dropdown-header"><wm-common-text class="dropdown-group-text" [data]="{ text: 'System Inspection Fields' }"></wm-common-text></div>
    <button
      *ngFor="let field of getFieldsForMapping(section, columnIndex, FieldKeyItemSource.Inspection)"
      type="button"
      ngbDropdownItem
      (click)="addFieldMapping(section, columnIndex, field); dropdown.close()"
    ><wm-common-text class="dropdown-option-text" [data]="{ text: field.label }"></wm-common-text></button>
  </ng-container>
  <ng-container *ngIf="getFieldsForMapping(section, columnIndex, FieldKeyItemSource.InspectionCustomFields).length">
    <div class="dropdown-header"><wm-common-text class="dropdown-group-text" [data]="{ text: 'Custom Inspection Fields' }"></wm-common-text></div>
    <button
      *ngFor="let field of getFieldsForMapping(section, columnIndex, FieldKeyItemSource.InspectionCustomFields)"
      type="button"
      ngbDropdownItem
      (click)="addFieldMapping(section, columnIndex, field); dropdown.close()"
    ><wm-common-text class="dropdown-option-text" [data]="{ text: getInspectionCustomFieldOptionLabel(field) }"></wm-common-text></button>
  </ng-container>
  <div *ngIf="!hasAnyAvailableFields(section, columnIndex)" class="no-field-options">
    <wm-common-text class="no-field-text" [data]="{ text: 'No more fields available.' }"></wm-common-text>
  </div>
</ng-template>

<!-- Added by Amisha on 2026-10-07.
     Reason: use the shared Janus mobile dialog layout for remove, discard, and delete confirmations. -->
<wm-common-confirmation-modal
  [data]="removeItemModal"
  [isApplicantMobileview]="true"
  (onYesClick)="confirmDeleteColumn()"
  (onNoClick)="cancelDeleteColumn()"
></wm-common-confirmation-modal>

<wm-common-confirmation-modal
  [data]="discardChangesModal"
  [isApplicantMobileview]="true"
  (onYesClick)="confirmClose()"
  (onNoClick)="closeDiscardChangesModal()"
></wm-common-confirmation-modal>

<wm-common-confirmation-modal
  [data]="deleteConfigurationModal"
  [isApplicantMobileview]="true"
  (onYesClick)="confirmDeleteConfiguration()"
  (onNoClick)="closeDeleteConfigurationModal()"
></wm-common-confirmation-modal>
